import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Form,
  theme,
} from "antd";
import {
  SearchOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function TicketsSearch() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState(10);
  const [searchResults, setSearchResults] = useState([]);
  const [searched, setSearched] = useState(false);

  // No backend API for tickets yet - will be populated when Zendesk integration is implemented
  const allTickets = [];

  const handleSearch = (values) => {
    let results = [...allTickets];

    if (values.keyword) {
      const kw = values.keyword.toLowerCase();
      results = results.filter(
        (t) =>
          t.id.toLowerCase().includes(kw) ||
          t.title.toLowerCase().includes(kw) ||
          t.systemSn.toLowerCase().includes(kw) ||
          t.customer.toLowerCase().includes(kw)
      );
    }

    if (values.status && values.status.length > 0) {
      results = results.filter((t) => values.status.includes(t.status));
    }

    if (values.priority && values.priority.length > 0) {
      results = results.filter((t) => values.priority.includes(t.priority));
    }

    if (values.group) {
      results = results.filter((t) => t.group === values.group);
    }

    if (values.dateRange && values.dateRange[0] && values.dateRange[1]) {
      const start = values.dateRange[0].startOf("day");
      const end = values.dateRange[1].endOf("day");
      results = results.filter((t) => {
        const created = dayjs(t.createdAt);
        return created.isAfter(start) && created.isBefore(end);
      });
    }

    setSearchResults(results);
    setSearched(true);
  };

  const handleClear = () => {
    form.resetFields();
    setSearchResults([]);
    setSearched(false);
  };

  const getPriorityTag = (priority) => {
    const colors = { high: "red", medium: "orange", low: "blue" };
    return <Tag color={colors[priority]}>{priority.toUpperCase()}</Tag>;
  };

  const getStatusTag = (status) => {
    const config = {
      open: { color: "error", icon: <ExclamationCircleOutlined /> },
      ongoing: { color: "processing", icon: <ClockCircleOutlined /> },
      closed: { color: "success", icon: <CheckCircleOutlined /> },
    };
    return (
      <Tag color={config[status].color} icon={config[status].icon}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Tag>
    );
  };

  const columns = [
    {
      title: "Ticket ID",
      dataIndex: "id",
      key: "id",
      render: (id) => (
        <Button type="link" onClick={() => navigate(`/tickets/${id}`)} style={{ padding: 0 }}>
          {id}
        </Button>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
    },
    {
      title: "System",
      dataIndex: "systemSn",
      key: "systemSn",
      render: (sn) => (
        <Button type="link" onClick={() => navigate(`/systems/${sn}`)} style={{ padding: 0 }}>
          {sn}
        </Button>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
    },
    {
      title: "Group",
      dataIndex: "group",
      key: "group",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: getPriorityTag,
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: getStatusTag,
      width: 110,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
    },
  ];

  return (
    <PageLayout active="ticketsSearch">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          Search Tickets
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Find tickets by various criteria
        </Text>
      </div>

      {/* Search Form */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 24 }}>
        <Form form={form} layout="vertical" onFinish={handleSearch}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="keyword" label="Keyword">
                <Input
                  placeholder="Ticket ID, title, system SN, customer..."
                  prefix={<SearchOutlined />}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="status" label="Status">
                <Select
                  mode="multiple"
                  placeholder="Select status"
                  size="large"
                  options={[
                    { value: "open", label: "Open" },
                    { value: "ongoing", label: "Ongoing" },
                    { value: "closed", label: "Closed" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="priority" label="Priority">
                <Select
                  mode="multiple"
                  placeholder="Select priority"
                  size="large"
                  options={[
                    { value: "high", label: "High" },
                    { value: "medium", label: "Medium" },
                    { value: "low", label: "Low" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="group" label="Group">
                <Select
                  placeholder="Select group"
                  size="large"
                  allowClear
                  options={[
                    { value: "Sydney Metro", label: "Sydney Metro" },
                    { value: "Melbourne Central", label: "Melbourne Central" },
                    { value: "Brisbane North", label: "Brisbane North" },
                    { value: "Perth West", label: "Perth West" },
                    { value: "Adelaide Hills", label: "Adelaide Hills" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="dateRange" label="Created Date Range">
                <RangePicker size="large" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label=" ">
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />} size="large">
                    Search
                  </Button>
                  <Button onClick={handleClear} icon={<ClearOutlined />} size="large">
                    Clear
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Search Results */}
      {searched && (
        <Card
          bordered={false}
          style={{ borderRadius: 12 }}
          title={
            <Space>
              <Text strong>Search Results</Text>
              <Tag>{searchResults.length} tickets found</Tag>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={searchResults}
            rowKey="id"
            pagination={makePagination(pageSize, setPageSize)}
            locale={{
              emptyText: "No tickets found matching your criteria",
            }}
          />
        </Card>
      )}
    </PageLayout>
  );
}
